import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
  TextField,
  ThemeProvider,
  createTheme,
  StyledEngineProvider,
  Paper,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Save, 
  Download, 
  Upload, 
  Plus, 
  Trash, 
  Send,
  HelpCircle,
  FileText,
  Edit as EditIcon,
  Plus as AddIcon, 
  Send as SendIcon
} from 'lucide-react';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid'; // GridToolbarContainer, 
import PaymentPanel from './PaymentPanel.tsx';
import GradientText from '../common/GradientText.tsx';
import ProblemSourcingDrawer from '../workspace/ProblemSourcingDrawer.tsx';

// Create a dark theme for the DataGrid
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#8A2BE2',
    },
    secondary: {
      main: '#00CC99',
    },
    background: {
      default: 'rgba(26, 26, 46, 0.6)',
      paper: 'rgba(26, 26, 46, 0.8)',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
});

const SpreadsheetContainer = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 200px)',
  display: 'flex',
  flexDirection: 'column',
  background: 'rgba(26, 26, 46, 0.6)',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  border: '1px solid rgba(138, 43, 226, 0.2)',
}));

const Toolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
}));

const FooterToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderTop: '1px solid rgba(138, 43, 226, 0.2)',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  padding: theme.spacing(0.5),
  margin: theme.spacing(0, 0.5),
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.1)',
    color: '#fff',
  },
}));

const AIAssistButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
  color: '#fff',
  padding: theme.spacing(0.5, 2),
  borderRadius: theme.spacing(4),
  fontSize: '0.8rem',
  marginLeft: 'auto',
  '&:hover': {
    background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
    boxShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
  },
}));

// Custom toolbar for DataGrid
const CustomToolbar = () => {
  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="subtitle2" sx={{ flex: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
        Spreadsheet Editor
      </Typography>
    </Box>
  );
}

interface SpreadsheetEditorProps {
  initialData?: GridRowsProp;
  initialColumns?: GridColDef[];
  onSave?: (rows: GridRowsProp, columns: GridColDef[]) => void;
  onAddToProposal?: (rows: GridRowsProp, columns: GridColDef[], cost: number) => void;
  problemText?: string;
  setProblemText?: (text: string) => void;
}

const SpreadsheetEditor: React.FC<SpreadsheetEditorProps> = ({
  initialData = [],
  initialColumns = [],
  onSave,
  onAddToProposal,
  problemText,
  setProblemText
}) => {
  // Default columns if none provided
  const defaultColumns: GridColDef[] = initialColumns.length > 0 ? initialColumns : [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'name', headerName: 'Name', width: 150, editable: true },
    { field: 'description', headerName: 'Description', width: 250, editable: true },
    { field: 'quantity', headerName: 'Quantity', type: 'number', width: 120, editable: true },
    { field: 'unitPrice', headerName: 'Unit Price ($)', type: 'number', width: 150, editable: true },
    { field: 'total', headerName: 'Total ($)', width: 150, valueGetter: (params: any) => {
      const quantity = params.row.quantity || 0;
      const unitPrice = params.row.unitPrice || 0;
      return quantity * unitPrice;
    }},
  ];

  // Default rows if none provided
  const defaultRows: GridRowsProp = initialData.length > 0 ? initialData : [
    { id: 1, name: 'Item 1', description: 'Description for item 1', quantity: 2, unitPrice: 100 },
    { id: 2, name: 'Item 2', description: 'Description for item 2', quantity: 1, unitPrice: 250 },
    { id: 3, name: 'Item 3', description: 'Description for item 3', quantity: 5, unitPrice: 50 },
  ];

  // eslint-disable-next-line no-unused-vars
  const [columns, setColumns] = useState<GridColDef[]>(defaultColumns);
  const [rows, setRows] = useState<GridRowsProp>(defaultRows);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [aiCost, setAiCost] = useState(3500);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showProblemSourcingDrawer, setShowProblemSourcingDrawer] = useState(false);
  const [localProblemText, setLocalProblemText] = useState(problemText || '');

  useEffect(() => {
    setLocalProblemText(problemText || '');
  }, [problemText]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setLocalProblemText(newText);
    if (setProblemText) {
      setProblemText(newText);
    }
  };

  const safeSetShowProblemSourcingDrawer = (show: boolean) => {
    setShowProblemSourcingDrawer(show);
  };

  const handleAddRow = () => {
    const newId = Math.max(...rows.map(row => Number(row.id)), 0) + 1;
    const newRow = { id: newId, name: `New Item ${newId}`, description: '', quantity: 0, unitPrice: 0 };
    setRows([...rows, newRow]);
  };

  const handleDeleteRow = () => {
    // In a real app, you would delete selected rows
    if (rows.length > 1) {
      setRows(rows.slice(0, -1));
    }
  };

  const handleSaveDraft = () => {
    if (onSave) {
      onSave(rows, columns);
    }
    
    setSnackbarMessage('Spreadsheet draft saved successfully!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleExport = () => {
    // Create CSV content
    const headers = columns.map(col => col.headerName).join(',');
    const rowData = rows.map(row => 
      columns.map(col => row[col.field as keyof typeof row]).join(',')
    ).join('\n');
    const csvContent = `${headers}\n${rowData}`;
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'spreadsheet.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAddToProposal = () => {
    if (onAddToProposal) {
      // Calculate cost based on number of rows and columns
      const baseCost = 2000;
      const costPerCell = 50;
      const totalCells = rows.length * columns.length;
      const totalCost = baseCost + (totalCells * costPerCell);
      
      onAddToProposal(rows, columns, Math.round(totalCost));
      
      setSnackbarMessage(`Added to proposal (Cost: $${Math.round(totalCost).toLocaleString()})`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }
  };

  const handleAIAssistClick = () => {
    setAiDialogOpen(true);
  };

  const handleAIDialogClose = () => {
    setAiDialogOpen(false);
  };

  const handleAIRequest = () => {
    setAiDialogOpen(false);
    setPaymentDialogOpen(true);
  };

  const handlePaymentComplete = () => {
    setPaymentDialogOpen(false);
    
    // Simulate AI response by generating new rows or modifying existing ones
    setTimeout(() => {
      // Example AI-generated data
      const aiGeneratedRows: GridRowsProp = [
        { id: rows.length + 1, name: 'AI Item 1', description: 'AI generated description', quantity: 3, unitPrice: 200 },
        { id: rows.length + 2, name: 'AI Item 2', description: 'Another AI generated item', quantity: 2, unitPrice: 150 },
      ];
      
      setRows([...rows, ...aiGeneratedRows]);
      
      setSnackbarMessage('AI generated data added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }, 1000);
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadDialogOpen(true);
      // In a real application, you would parse the CSV/Excel file here
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Problem Input Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: 'rgba(26, 26, 46, 0.7)',
          borderRadius: 1,
          border: '1px solid rgba(138, 43, 226, 0.3)',
        }}
      >
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Start working on a new Problem"
          variant="outlined"
          value={localProblemText}
          onChange={handleTextChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Submit problem to LLM">
                  <IconButton 
                    edge="end" 
                    onClick={() => {
                      if (localProblemText) {
                        console.log('Sending to LLM:', localProblemText);
                      }
                    }}
                    disabled={!localProblemText}
                    sx={{
                      color: '#00CC99',
                      '&.Mui-disabled': {
                        color: 'rgba(0, 204, 153, 0.3)',
                      }
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            startAdornment: (
              <InputAdornment position="start">
                <Tooltip title="Open problem submission drawer">
                  <IconButton 
                    edge="start" 
                    onClick={() => safeSetShowProblemSourcingDrawer(true)}
                    sx={{ color: 'rgba(138, 43, 226, 0.7)' }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            sx: {
              backgroundColor: 'rgba(26, 26, 46, 0.4)',
              color: '#fff',
              '&.Mui-focused': {
                boxShadow: '0 0 0 2px rgba(138, 43, 226, 0.5)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(138, 43, 226, 0.3)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(138, 43, 226, 0.5)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(138, 43, 226, 0.7)',
              },
            }
          }}
          sx={{
            '& .MuiInputBase-input': {
              color: '#fff',
            },
          }}
        />
        
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => safeSetShowProblemSourcingDrawer(true)}
          sx={{
            mt: 1,
            borderColor: 'rgba(138, 43, 226, 0.5)',
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              borderColor: '#8A2BE2',
              backgroundColor: 'rgba(138, 43, 226, 0.1)',
            }
          }}
        >
          <GradientText gradient="secondary" variant="body2">
            Use Advanced Problem Sourcing
          </GradientText>
        </Button>
      </Paper>

      <Typography variant="caption" sx={{ mt: 1, mb: 2, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
        Describe your problem or paste existing text to get started
      </Typography>

      <SpreadsheetContainer>
        <Toolbar>
          <Box display="flex" alignItems="center">
            <Tooltip title="Add Row">
              <StyledIconButton onClick={handleAddRow}>
                <Plus size={18} />
              </StyledIconButton>
            </Tooltip>
            <Tooltip title="Delete Row">
              <StyledIconButton onClick={handleDeleteRow}>
                <Trash size={18} />
              </StyledIconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 1, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
            <Tooltip title="Upload Spreadsheet">
              <StyledIconButton onClick={handleUploadClick}>
                <Upload size={18} />
              </StyledIconButton>
            </Tooltip>
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              accept=".csv,.xlsx,.xls" 
              onChange={handleFileSelected} 
            />
          </Box>
          
          <AIAssistButton 
            startIcon={<HelpCircle size={16} />}
            onClick={handleAIAssistClick}
          >
            AI Data Generation
          </AIAssistButton>
        </Toolbar>
        
        <Box sx={{ flex: 1, width: '100%', height: 'calc(100% - 110px)' }}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={darkTheme}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSizeOptions={[5, 10, 25]}
                checkboxSelection
                disableRowSelectionOnClick
                autoHeight={false}
                sx={{
                  height: '100%',
                }}
                slots={{ toolbar: CustomToolbar }}
              />
            </ThemeProvider>
          </StyledEngineProvider>
        </Box>
        
        <FooterToolbar>
          <Box>
            <Button
              variant="outlined"
              startIcon={<Save size={16} />}
              onClick={handleSaveDraft}
              sx={{ 
                borderColor: 'rgba(138, 43, 226, 0.5)',
                color: '#fff',
                mr: 1,
                '&:hover': {
                  borderColor: 'rgba(138, 43, 226, 0.8)',
                  background: 'rgba(138, 43, 226, 0.1)',
                }
              }}
            >
              Save Draft
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download size={16} />}
              onClick={handleExport}
              sx={{ 
                borderColor: 'rgba(0, 204, 153, 0.5)',
                color: '#00CC99',
                '&:hover': {
                  borderColor: 'rgba(0, 204, 153, 0.8)',
                  background: 'rgba(0, 204, 153, 0.1)',
                }
              }}
            >
              Export CSV
            </Button>
          </Box>
          
          <Button
            variant="contained"
            endIcon={<Send size={16} />}
            onClick={handleAddToProposal}
            sx={{
              background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
              color: '#fff',
              '&:hover': {
                background: 'linear-gradient(45deg, #00CC99 40%, #8A2BE2 100%)',
                boxShadow: '0 0 10px rgba(0, 204, 153, 0.5)',
              },
            }}
          >
            Add to Proposal
          </Button>
        </FooterToolbar>
      </SpreadsheetContainer>
      
      {/* AI Assist Dialog */}
      <Dialog open={aiDialogOpen} onClose={handleAIDialogClose} maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HelpCircle size={20} style={{ marginRight: 8 }} />
            AI Data Generation Assistant
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Describe what kind of data you would like the AI to generate for your spreadsheet:
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            placeholder="E.g., Generate a price comparison table for cloud providers with services, features, and monthly costs..."
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
          />
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mt: 3, 
            p: 2, 
            bgcolor: 'rgba(0, 204, 153, 0.1)', 
            borderRadius: 1 
          }}>
            <Typography variant="body2">
              AI Data Generation Cost:
            </Typography>
            <Typography variant="h6" sx={{ color: '#00CC99', fontWeight: 600 }}>
              ${aiCost.toLocaleString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAIDialogClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleAIRequest} 
            variant="contained"
            disabled={!aiPrompt.trim()}
            sx={{
              background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
              color: '#fff',
            }}
          >
            Process Request
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FileText size={20} style={{ marginRight: 8 }} />
            Complete Payment for AI Data Generation
          </Box>
        </DialogTitle>
        <DialogContent>
          <PaymentPanel 
            proposalId={null} 
            solutions={[]} 
            onPaymentComplete={handlePaymentComplete}
          />
        </DialogContent>
      </Dialog>
      
      {/* File Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload Spreadsheet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a CSV or Excel file to import into the spreadsheet.
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            p: 3,
            border: '2px dashed rgba(138, 43, 226, 0.3)',
            borderRadius: 2,
            my: 2
          }}>
            <Upload size={40} color="#8A2BE2" />
            <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
              Drag and drop or click to browse
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
              color: '#fff',
            }}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%',
            background: snackbarSeverity === 'success' ? 'rgba(0, 204, 153, 0.2)' : 'rgba(244, 67, 54, 0.2)',
            color: snackbarSeverity === 'success' ? '#00CC99' : '#f44336',
            border: `1px solid ${snackbarSeverity === 'success' ? 'rgba(0, 204, 153, 0.5)' : 'rgba(244, 67, 54, 0.5)'}`,
            backdropFilter: 'blur(10px)',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {showProblemSourcingDrawer && (
        <ProblemSourcingDrawer 
          onSubmit={(problem) => {
            setLocalProblemText(problem);
            if (setProblemText) {
              setProblemText(problem);
            }
            safeSetShowProblemSourcingDrawer(false);
          }}
        />
      )}
    </Box>
  );
};

export default SpreadsheetEditor;